import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpContext } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import config from '../../../config';
import { SKIP_LOADER } from '../../interceptors/loader-interceptor';

interface ApiWorkerResponse {
  result_header?: {
    total?: number;
  };
  workers?: {
    prid?: string;
    worker_id?: string;
    name?: {
      display_name?: string;
      preferred_first_name?: string;
      preferred_last_name?: string;
    };
    business_contact?: {
      work_email?: string;
      work_phone?: string;
      mobile?: string;
    };
    manager?: {
      manager_prid?: string;
      manager_display_name?: string;
      manager_work_email?: string;
    };
    status?: string;
    worker_type?: string;
    worker_sub_type?: string;
  }[];
}

interface WorkerApiResponse {
  workers?: {
    prid?: string;
    workerId?: string;
    id?: string;
    name?: {
      displayName?: string;
      firstName?: string;
      lastName?: string;
    };
    businessContact?: { workEmail?: string };
    manager?: { managerPrid?: string };
  }[];
}

@Injectable({ providedIn: 'root' })
export class WorkerMasterService {

  private http = inject(HttpClient);
  private readonly apiBase = `${config.API_GATEWAY_ENV_ENDPOINT}/workers`;

  fetchWorkers(options?: {
    first_name_contains?: string;
    last_name_contains?: string;
  }): Observable<WorkerApiResponse> {

    let params = new HttpParams();

    if (options?.first_name_contains) {
      params = params.set('first_name_contains', options.first_name_contains);
    }
    if (options?.last_name_contains) {
      params = params.set('last_name_contains', options.last_name_contains);
    }

    return this.http.get<ApiWorkerResponse>(this.apiBase, { params }).pipe(
      map((response: ApiWorkerResponse) => {
        const transformedWorkers = response.workers?.map(worker => ({
          prid: worker.prid,
          workerId: worker.worker_id,
          id: worker.worker_id,
          name: {
            displayName: worker.name?.display_name,
            firstName: worker.name?.preferred_first_name,
            lastName: worker.name?.preferred_last_name
          },
          businessContact: {
            workEmail: worker.business_contact?.work_email
          },
          manager: {
            managerPrid: worker.manager?.manager_prid
          }
        }));

        return { workers: transformedWorkers } as WorkerApiResponse;
      }),
      catchError((err: unknown) => {
        console.error('Error fetching from workers API:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Get managers under a specific worker PRID for a market
   * Returns the worker and all managers under them
   */
  getManagersByPrid(managerPrid: string, marketId: number, skipLoader = false): Observable<{
    workers: {
      prid?: string;
      workerId?: string;
      id?: string;
      name?: {
        displayName?: string;
        firstName?: string;
        lastName?: string;
      };
      businessContact?: { workEmail?: string };
      manager?: { managerPrid?: string };
      is_system_manager?: string; // 'Y' or 'N'
    }[]
  }> {
    const params = new HttpParams()
      .set('manager_prid', managerPrid)
      .set('market_id', marketId.toString());

    const options = skipLoader
      ? { params, context: new HttpContext().set(SKIP_LOADER, true) }
      : { params };

    return this.http.get<{
      workers?: {
        prid?: string;
        worker_id?: string;
        name?: {
          display_name?: string;
          preferred_first_name?: string;
          preferred_last_name?: string;
        };
        business_contact?: { work_email?: string };
        manager?: { manager_prid?: string };
        job?: { is_system_manager?: string };
      }[]
    }>(`${this.apiBase}/managers`, options).pipe(
      map((response) => {
        const transformedWorkers = response.workers?.map(worker => ({
          prid: worker.prid,
          workerId: worker.worker_id,
          id: worker.worker_id,
          name: {
            displayName: worker.name?.display_name,
            firstName: worker.name?.preferred_first_name,
            lastName: worker.name?.preferred_last_name
          },
          businessContact: {
            workEmail: worker.business_contact?.work_email
          },
          manager: {
            managerPrid: worker.manager?.manager_prid
          },
          is_system_manager: worker.job?.is_system_manager
        }));

        return { workers: transformedWorkers || [] };
      }),
      catchError((err: unknown) => {
        console.error('Error fetching managers from workers API:', err);
        return throwError(() => err);
      })
    );
  }
}
